// Returns → Registered (Returns project 2 Phase D, 28.10): the returns customers announced — in the portal (Phase E) or
// through customer service here. A registration never moves money (the desk prices the parcel); an exchange line holds
// its new size from registration and ships when the return arrives; 21 days with nothing on its way and the size goes
// back. Customer service can register for a customer, mark it sent, or cancel it. Backend: return_registrations.py.
// -- Claude, 2026-10-28
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { OrderNo } from '../OrderNo';
import { fmtWh } from '../time';

const R = `${API}/returns`;

const STATUS: Record<string, { label: string; cls: string }> = {
  registered: { label: 'Registered', cls: 'bg-blue-900/60 text-blue-200' },
  on_its_way: { label: 'On its way', cls: 'bg-indigo-900/60 text-indigo-200' },
  arrived: { label: 'Partly back', cls: 'bg-amber-900/60 text-amber-200' },
  done: { label: 'Back', cls: 'bg-green-900/60 text-green-300' },
  expired: { label: 'Expired', cls: 'bg-gray-700 text-gray-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-700 text-gray-400' },
};

const detailOf = async (r: Response) => {
  const d = await r.json().catch(() => ({}));
  return typeof d.detail === 'string' ? d.detail : d.detail?.message || `HTTP ${r.status}`;
};

export function RegisteredTab() {
  const [state, setState] = useState<'open' | 'closed'>('open');
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [registering, setRegistering] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch(`${R}/registrations?state=${state}`);
    if (!r.ok) { setErr(await detailOf(r)); setRows([]); return; }
    setErr('');
    setRows((await r.json()).registrations || []);
  }, [state]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const act = async (row: any, path: string, body: any) => {
    setBusy(row.id);
    try {
      const r = await authFetch(`${R}/registrations/${row.id}/${path}`, { method: 'POST', body: JSON.stringify(body) });
      if (!r.ok) { const why = await detailOf(r); setMsg(m => ({ ...m, [row.id]: `⚠ ${why}` })); return; }
      setMsg(m => ({ ...m, [row.id]: '' }));
      load();
    } finally { setBusy(null); }
  };
  const markSent = (row: any) => {
    const tn = window.prompt(`Return ${row.code}: the customer has sent it.\n\nTracking number (optional):`, row.tracking_number || '');
    if (tn !== null) act(row, 'sent', { tracking_number: tn.trim() || null });
  };
  const cancel = (row: any) => {
    const held = (row.exchanges || []).some((e: any) => e.status === 'open' && e.reserved);
    const note = window.prompt(`Cancel return ${row.code} on order ${row.order_number}?` +
      (held ? '\n\n• the size held for its exchange goes back on sale' : '') + '\n\nWhy? (required)');
    if (note === null) return;
    if (!note.trim()) { setMsg(m => ({ ...m, [row.id]: '⚠ Say why.' })); return; }
    act(row, 'cancel', { note: note.trim() });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-gray-500 max-w-3xl">
          {state === 'open'
            ? 'Returns the customer announced. An exchange holds its new size and ships when the return arrives; 21 days with nothing on its way and the size goes back on sale. A registration moves no money — the desk prices the parcel.'
            : 'Registrations closed in the last 60 days.'}
        </p>
        <div className="flex items-center gap-3">
          <button onClick={() => setRegistering(v => !v)} className="px-3 py-1.5 rounded bg-blue-700 hover:bg-blue-600 text-sm text-white">
            {registering ? 'Close' : 'Register a return'}
          </button>
          <button onClick={() => setState(state === 'open' ? 'closed' : 'open')} className="text-sm text-gray-400 hover:text-white">
            {state === 'open' ? 'Show closed →' : '← Open'}
          </button>
        </div>
      </div>
      {registering && <RegisterForm onDone={() => { setRegistering(false); load(); }} />}
      {err ? <div className="text-amber-400 text-sm">⚠ {err}</div>
        : rows === null ? <div className="text-gray-600 text-sm">Loading…</div>
        : rows.length === 0 ? (
          <div className="text-gray-600 text-sm py-8 text-center border border-dashed border-gray-800 rounded-lg">
            {state === 'open' ? 'No registered returns waiting.' : 'Nothing closed in the last 60 days.'}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(row => {
              const st = STATUS[row.status] || STATUS.registered;
              const ex = (row.exchanges || [])[0];
              return (
                <div key={row.id} className="bg-[#1a1d27] border border-gray-800 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-white">{row.code}</span>
                    <OrderNo value={row.order_number} prefix="Order " />
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    <span className="text-xs text-gray-500">{row.via === 'portal' ? 'by the customer' : `by ${row.created_by}`} · {fmtWh(row.created_at, 'short')}</span>
                    {ex && <span className="text-xs text-gray-500">exchange E{ex.id} ({ex.status})</span>}
                    {row.tracking_number && <span className="text-xs text-gray-400">tracking {row.tracking_number}</span>}
                    {['registered', 'on_its_way'].includes(row.status) && (
                      <span className="text-xs text-gray-500 ml-auto">held until {fmtWh(row.hold_until, 'date')}</span>
                    )}
                  </div>
                  <ul className="mt-2 space-y-0.5 text-xs text-gray-300">
                    {(row.lines || []).map((l: any) => (
                      <li key={l.id}>
                        {l.qty}× {l.article_number || l.product_name} —{' '}
                        {l.intent === 'exchange'
                          ? (l.effective_intent === 'exchange' ? <span>exchange for {l.wanted_article}</span>
                            : <span className="text-amber-300">exchange for {l.wanted_article} → refund ({l.effective_why})</span>)
                          : 'refund'}
                        {l.defect && <span className="ml-1 text-red-300">· faulty</span>}
                        {l.reason && <span className="ml-1 text-gray-500">· {l.reason}</span>}
                        <span className="ml-1 text-gray-500">· back {l.qty_arrived}/{l.qty}</span>
                      </li>
                    ))}
                  </ul>
                  {row.release_error && <div className="mt-1 text-xs text-amber-300">⚠ {row.release_error}</div>}
                  {row.close_note && <div className="mt-1 text-xs text-gray-500">{row.closed_by}: {row.close_note}</div>}
                  {['registered', 'on_its_way'].includes(row.status) && (
                    <div className="mt-2 flex gap-2">
                      <button disabled={busy === row.id} onClick={() => markSent(row)}
                        className="px-2.5 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-gray-200 disabled:opacity-40">Customer has sent it</button>
                      <button disabled={busy === row.id} onClick={() => cancel(row)}
                        className="px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-xs text-gray-400 hover:text-red-300 disabled:opacity-40">Cancel</button>
                    </div>
                  )}
                  {msg[row.id] && <div className="mt-1 text-xs text-gray-300">{msg[row.id]}</div>}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

type Pick = { qty: number; intent: 'refund' | 'exchange'; wanted: string; defect: boolean; reason: string };

function RegisterForm({ onDone }: { onDone: () => void }) {
  const [number, setNumber] = useState('');
  const [opts, setOpts] = useState<any | null>(null);
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const find = async () => {
    setErr(''); setOpts(null); setPicks({});
    if (!number.trim()) return;
    const r = await authFetch(`${R}/registrations/options?order_number=${encodeURIComponent(number.trim())}`);
    if (!r.ok) { setErr(await detailOf(r)); return; }
    setOpts(await r.json());
  };
  const blank: Pick = { qty: 0, intent: 'refund', wanted: '', defect: false, reason: '' };
  const set = (vid: string, p: Partial<Pick>) =>
    setPicks(m => ({ ...m, [vid]: { ...blank, ...m[vid], ...p } }));
  const chosen = Object.entries(picks).filter(([, p]) => p.qty > 0);
  const submit = async () => {
    if (!chosen.length) { setErr('Choose at least one piece.'); return; }
    if (chosen.some(([, p]) => p.intent === 'exchange' && !p.wanted)) { setErr('Choose the size for every exchange.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${R}/registrations`, { method: 'POST', body: JSON.stringify({
        order_number: number.trim(),
        lines: chosen.map(([vid, p]) => ({ variation_id: vid, qty: p.qty, intent: p.intent,
          wanted_variation_id: p.intent === 'exchange' ? p.wanted : null, defect: p.defect, reason: p.reason.trim() || null })),
      }) });
      if (!r.ok) { setErr(await detailOf(r)); return; }
      onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 bg-[#141722] border border-gray-800 rounded-lg p-4 text-sm">
      <div className="flex gap-2 items-center">
        <input value={number} onChange={e => setNumber(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') find(); }}
          placeholder="Order number" className="px-3 py-1.5 rounded bg-gray-900 border border-gray-700 text-gray-100" />
        <button onClick={find} className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-100">Find</button>
      </div>
      {opts && (
        <div className="mt-3">
          <div className="text-xs text-gray-400 mb-2">
            {opts.delivered_at ? `Delivered ${fmtWh(opts.delivered_at, 'date')} · the 14 days ${opts.window_open ? 'end' : 'ended'} ${fmtWh(opts.window_ends_at, 'date')}`
              : 'No delivery date known'}
            {!opts.window_open && ' — customer service may still register it'}
          </div>
          {opts.blocked && <div className="text-amber-300 text-xs mb-2">⚠ {opts.blocked}</div>}
          {opts.active && <div className="text-amber-300 text-xs mb-2">⚠ Return {opts.active.code} is already registered for this order.</div>}
          <div className="space-y-2">
            {(opts.lines || []).map((l: any) => {
              const p = picks[l.variation_id] || { qty: 0, intent: 'refund', wanted: '', defect: false, reason: '' };
              return (
                <div key={l.variation_id} className="flex items-center gap-2 flex-wrap">
                  <span className="w-56 truncate text-gray-200" title={l.product_name}>{l.article_number || l.product_name}</span>
                  {l.can_register === 0 ? <span className="text-xs text-gray-500">nothing left to return</span> : (
                    <>
                      <select value={p.qty} onChange={e => set(l.variation_id, { qty: Number(e.target.value) })}
                        className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5">
                        {Array.from({ length: l.can_register + 1 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                      </select>
                      <select value={p.intent} onChange={e => set(l.variation_id, { intent: e.target.value as any })}
                        className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5">
                        <option value="refund">refund</option>
                        {l.exchange_sizes.length > 0 && <option value="exchange">exchange for</option>}
                      </select>
                      {p.intent === 'exchange' && (
                        <select value={p.wanted} onChange={e => set(l.variation_id, { wanted: e.target.value })}
                          className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5">
                          <option value="">— size —</option>
                          {l.exchange_sizes.map((s: any) => <option key={s.variation_id} value={s.variation_id}>{s.size || s.article_number} ({s.stock_qty})</option>)}
                        </select>
                      )}
                      <label className="text-xs text-gray-400 flex items-center gap-1">
                        <input type="checkbox" checked={p.defect} onChange={e => set(l.variation_id, { defect: e.target.checked })} /> faulty
                      </label>
                      <input value={p.reason} onChange={e => set(l.variation_id, { reason: e.target.value })} placeholder="reason"
                        className="flex-1 min-w-[8rem] px-2 py-0.5 rounded bg-gray-900 border border-gray-700 text-gray-100" />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button disabled={busy || !!opts.blocked || !!opts.active} onClick={submit}
              className="px-3 py-1.5 rounded bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-40">Register</button>
            <span className="text-xs text-gray-500">Free exchanges only (the same article and price, in stock). Anything else: the order's Return… composer.</span>
          </div>
        </div>
      )}
      {err && <div className="mt-2 text-amber-300 text-xs">⚠ {err}</div>}
    </div>
  );
}
